/**
 * `useCollectionState(conversationId)` — TanStack Query wrapper for
 * `GET /api/conversations/:id/collection-state` (next-variable-curation Phase 6).
 *
 * Provides the durable server-computed CollectionState so the NEXT banner
 * persists between turns and on load. The live SSE carrier
 * (`latestToolPayloads.collectionState` in builder.tsx) takes precedence
 * while a stream is in-flight; this hook is the fallback that survives
 * post-turn message refetches.
 *
 * Cache strategy:
 *   - `enabled: !!conversationId` — undefined/empty id never fires the query.
 *   - `staleTime: 0` — refetches on mount (matches useConversationViews);
 *     the builder route invalidates on the true→false `isCurrentlyStreaming`
 *     edge so the value reflects the just-finished turn's mutations.
 *   - `gcTime: 5 * 60_000` — 5-min retention after last subscriber unmounts.
 *
 * Key is nested under `conversations.all` via `conversations.collectionState(id)`
 * so any `conversations.all` invalidation sweeps this cache.
 */
import { useQuery } from '@tanstack/react-query';
import type { CollectionState } from '@arcanum-hq/shared-types';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export function useCollectionState(conversationId: string | undefined) {
  return useQuery<{ collectionState: CollectionState }>({
    queryKey: conversationId
      ? queryKeys.conversations.collectionState(conversationId)
      : ['conversations', '__no-id__', 'collection-state'],
    queryFn: () => api.getCollectionState(conversationId!),
    enabled: !!conversationId,
    staleTime: 0,
    gcTime: 5 * 60_000,
  });
}
